import type { ProductType } from "@/types/product";
import { setGtmDataLayer } from "@/utils/gtmDataLayer";

const APP_SOURCE = "bd-mobile-next";
const PURCHASE_STORAGE_PREFIX = "ga4_purchase_";

export interface GAItem {
  item_id: number | string;
  item_name: string;
  quantity: number;
  price: number;
  affiliation?: string;
  coupon?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  google_business_vertical?: "retail";
}

interface EcommerceParams {
  currency?: string;
  value?: number;
  coupon?: string;
  shipping?: number;
  tax?: number;
  transaction_id?: string;
  shipping_tier?: string;
  payment_type?: string;
  item_list_id?: string;
  item_list_name?: string;
  items: GAItem[];
}

const pushGA4EcommerceEvent = (
  eventName: string,
  ecommerce: EcommerceParams,
) => {
  // Data Layer Version 2 会合并对象，先清空可避免沿用上一次事件的商品字段。
  setGtmDataLayer({ ecommerce: null });
  return setGtmDataLayer({
    event: eventName,
    app_source: APP_SOURCE,
    ecommerce,
  });
};

const pushGA4Event = (
  eventName: string,
  parameters: Record<string, unknown> = {},
) => {
  return setGtmDataLayer({
    event: eventName,
    app_source: APP_SOURCE,
    ...parameters,
  });
};

export const gaItemFromProduct = (
  product: ProductType,
  options: {
    index?: number;
    itemListId?: string;
    itemListName?: string;
    itemCategory?: string;
  } = {},
): GAItem => ({
  item_id: product.productID,
  item_name: product.name,
  price: Number(product.price) || 0,
  quantity: 1,
  ...(product.productCode ? { item_variant: product.productCode } : {}),
  ...(options.itemCategory ? { item_category: options.itemCategory } : {}),
  ...(options.itemListId ? { item_list_id: options.itemListId } : {}),
  ...(options.itemListName ? { item_list_name: options.itemListName } : {}),
  ...(options.index !== undefined ? { index: options.index } : {}),
  google_business_vertical: "retail",
});

export const gaViewItem = ({
  currency = "NZD",
  value,
  items,
}: {
  currency?: string;
  value: number;
  items: GAItem[];
}) => {
  pushGA4EcommerceEvent("view_item", { currency, value, items });
};

export const gaViewItemList = ({
  item_list_id,
  item_list_name,
  items,
}: {
  item_list_id: string;
  item_list_name: string;
  items: GAItem[];
}) => {
  pushGA4EcommerceEvent("view_item_list", {
    item_list_id,
    item_list_name,
    items,
  });
};

export const gaSelectItem = ({
  item_list_id,
  item_list_name,
  items,
}: {
  item_list_id?: string;
  item_list_name?: string;
  items: GAItem[];
}) => {
  pushGA4EcommerceEvent("select_item", {
    ...(item_list_id ? { item_list_id } : {}),
    ...(item_list_name ? { item_list_name } : {}),
    items,
  });
};

export const gaAddToCart = (
  currency: string = "NZD",
  value: number,
  productGroup: {
    productID: number | string;
    productName: string;
    quantity: number;
    price: number;
    productCode?: string;
    categoryName?: string;
  }[],
) => {
  const items: GAItem[] = productGroup.map((item) => ({
    item_id: item.productID,
    item_name: item.productName,
    quantity: Number(item.quantity) || 1,
    price: Number(item.price) || 0,
    ...(item.productCode ? { item_variant: item.productCode } : {}),
    ...(item.categoryName ? { item_category: item.categoryName } : {}),
    google_business_vertical: "retail",
  }));

  pushGA4EcommerceEvent("add_to_cart", { currency, value, items });
};

export const gaRemoveFromCart = ({
  currency = "NZD",
  value,
  items,
}: {
  currency?: string;
  value: number;
  items: GAItem[];
}) => {
  pushGA4EcommerceEvent("remove_from_cart", { currency, value, items });
};

export const gaViewCart = ({
  currency = "NZD",
  value,
  items,
}: {
  currency?: string;
  value: number;
  items: GAItem[];
}) => {
  pushGA4EcommerceEvent("view_cart", { currency, value, items });
};

export interface GAPurchaseParams {
  currency?: string;
  transaction_id: string;
  value: number;
  coupon?: string;
  shipping?: number;
  tax?: number;
  items: GAItem[];
}

export const gaPurchase = ({
  currency = "NZD",
  transaction_id,
  value,
  coupon = "",
  shipping = 0,
  tax = 0,
  items,
}: GAPurchaseParams) => {
  const storageKey = `${PURCHASE_STORAGE_PREFIX}${transaction_id}`;

  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // 隐私模式下 sessionStorage 可能不可用，transaction_id 仍可供 GA4 去重。
  }

  const pushed = pushGA4EcommerceEvent("purchase", {
    currency,
    transaction_id,
    value,
    coupon,
    shipping,
    tax,
    items,
  });

  if (pushed) {
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch {
      // 不因存储不可用阻断订单成功页。
    }
  }
};

export interface GABeginCheckoutParams {
  currency?: string;
  value: number;
  coupon?: string;
  items: GAItem[];
}

export const gaBeginCheckout = ({
  currency = "NZD",
  value,
  coupon = "",
  items,
}: GABeginCheckoutParams) => {
  pushGA4EcommerceEvent("begin_checkout", {
    currency,
    value,
    coupon,
    items,
  });
};

export const gaAddShippingInfo = ({
  currency = "NZD",
  value,
  coupon = "",
  shipping_tier,
  items,
}: GABeginCheckoutParams & { shipping_tier?: string }) => {
  pushGA4EcommerceEvent("add_shipping_info", {
    currency,
    value,
    coupon,
    shipping_tier,
    items,
  });
};

export const gaAddPaymentInfo = ({
  currency = "NZD",
  value,
  coupon = "",
  payment_type,
  items,
}: GABeginCheckoutParams & { payment_type?: string }) => {
  pushGA4EcommerceEvent("add_payment_info", {
    currency,
    value,
    coupon,
    payment_type,
    items,
  });
};

export const gaSearch = (searchTerm: string) => {
  if (!searchTerm.trim()) return;
  pushGA4Event("search", { search_term: searchTerm.trim() });
};

export const gaLogin = (method: string) => {
  pushGA4Event("login", { method });
};

export const gaSignUp = (method: string) => {
  pushGA4Event("sign_up", { method });
};
